import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import { GetDBECERT } from '../../../redux/actions/certification/dbeActions';

const statusChipColor = (status) => {
  switch (status) {
    case "ACCEPTED": return "success";
    case "PENDING":  return "warning";
    default:         return "default";
  }
};

const DBEPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.certification);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [dbeList, setDbeList] = useState([]);
  const [dbeTotal, setDbeTotal] = useState(0);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id",               sort: "id" },
    { label: "Authority Id",     sort: "authority_id" },
    { label: "Email",            sort: "email" },
    { label: "Certification Id", sort: "certification_id" },
    { label: "User Id",          sort: "user_id" },
    { label: "Status",           sort: "status" },
    { label: "Time stamps",      sort: "created_at" },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetDBE();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setDbeList(state.dbeList.records || []);
      setDbeTotal(state.dbeList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetDBE() {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&field=${sort}`;
    if (sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetDBECERT(page + 1, rowsPerPage, query, setLoading));
  };

  return (
    <Box>

      <TopBar
        title={"DBE"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          dbeList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No DBE records found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                dbeList.map((row, index) => (
                  <TableRow
                    key={index}
                    className="table__clickable-row">
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        {row.id || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        {row.authority_id || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.email || <NA />}</TableCell>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        {row.certification_id || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        {row.user_id || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {
                        row.status
                        ?
                        <Chip
                          variant="filled"
                          size="small"
                          color={statusChipColor(row.status)}
                          label={row.status.replace(/_/g, " ")}
                        />
                        :
                        <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                      />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={7}
                  count={dbeTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

    </Box>
  );
};

export default DBEPage;
